import { supabase } from './supabase';

export type AssessmentType = 'pilot' | 'instructor' | 'debriefing_guide';

export interface AssessmentVersion {
  id: string;
  assessment_id: string | null;
  /** Set for session-level pilot/instructor versions; null for legacy per-row AI baselines. */
  session_id: string | null;
  assessment_type: AssessmentType;
  version_number: number;
  snapshot: Record<string, unknown>;
  is_ai_baseline: boolean;
  created_by: string | null;
  created_at: string;
  change_summary: string | null;
  /**
   * MNT-33, migration 293. Set when a later AI baseline replaced this one; NULL
   * means this row is current. Regeneration supersedes rather than deleting, so
   * a session accumulates a chain of AI baselines with exactly one live at a
   * time. Optional on the type because rows read from a host that has not yet
   * had 293 applied carry neither column.
   */
  superseded_at?: string | null;
  superseded_by?: string | null;
}

export interface AssessmentUpdateFields {
  score?: number | null;
  analysis?: string | null;
  goals?: string | null;
  evidence?: unknown;
  // HG-3A G3-02b, pilot only, carried as keys on the batch item that migration
  // 249 reads. The dimensions group and the override group are emitted whole or
  // not at all; see buildBatchUpdates for why.
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  override_actor?: string | null;
  override_at?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  development_level_assigned_by?: string | null;
  development_level_assigned_at?: string | null;
  performance_criteria?: unknown;
  framework_id?: string | null;
  framework_version?: string | null;
}

/**
 * Batch-item keys forwarded verbatim to migration 249. Listed rather than
 * spread so an unrelated field added to AssessmentUpdateFields never reaches
 * the RPC by accident.
 */
const EXTENDED_BATCH_KEYS = [
  'how_many',
  'how_often',
  'tem_outcome',
  'limiting_factor',
  'proposed_grade',
  'override_flag',
  'override_category',
  'override_note',
  'override_actor',
  'override_at',
  'development_level',
  'development_level_source',
  'development_level_assigned_by',
  'development_level_assigned_at',
  'performance_criteria',
  'framework_id',
  'framework_version',
] as const;

export type BatchAssessmentUpdate = { assessmentId: string } & AssessmentUpdateFields;

/** Maps web batch updates to RPC JSON (omits unset fields — critical for evidence). */
export function buildRpcBatchUpdateItems(
  batchUpdates: BatchAssessmentUpdate[],
): Record<string, unknown>[] {
  return batchUpdates.map((u) => {
    const item: Record<string, unknown> = { assessment_id: u.assessmentId };
    if (u.score !== undefined) item.score = u.score;
    if (u.analysis !== undefined) item.analysis = u.analysis;
    if (u.goals !== undefined) item.goals = u.goals;
    if (u.evidence !== undefined) item.evidence = u.evidence;
    // Omitting an unset key matters as much here as it does for evidence:
    // migration 249 COALESCEs a missing key to the stored value, so sending
    // an explicit undefined-as-null would blank a field nobody edited.
    for (const key of EXTENDED_BATCH_KEYS) {
      const value = u[key];
      if (value !== undefined) item[key] = value;
    }
    return item;
  });
}

/** Synthetic version_number for aggregated per-row AI baselines in session history UI. */
export const AI_BASELINE_VERSION_NUMBER = 0;

export const AI_BASELINE_COMPOSITE_ID = 'ai-baseline-composite';

type SessionAiBaselineRow = {
  snapshot: Record<string, unknown>;
  created_at: string;
};

type PilotAiBaselineRow = SessionAiBaselineRow & {
  pilot_role: string | null;
  competency: string;
};

type InstructorAiBaselineRow = SessionAiBaselineRow & {
  criterion: string;
};

/** Fields compared in VersionHistoryDrawer session diffs. */
export function extractVersionFields(
  snapshot: Record<string, unknown>,
): Record<string, unknown> {
  return {
    score: snapshot.score,
    analysis: snapshot.analysis,
    goals: snapshot.goals,
    evidence: snapshot.evidence,
  };
}

export function buildPilotAiCompositeSnapshot(
  rows: PilotAiBaselineRow[],
): Record<string, unknown> {
  const composite: Record<string, Record<string, Record<string, unknown>>> = {};
  for (const row of rows) {
    const role = row.pilot_role ?? 'unknown';
    if (!composite[role]) composite[role] = {};
    composite[role][row.competency] = extractVersionFields(row.snapshot);
  }
  return composite;
}

export function buildInstructorAiCompositeSnapshot(
  rows: InstructorAiBaselineRow[],
): Record<string, unknown> {
  const composite: Record<string, Record<string, unknown>> = {};
  for (const row of rows) {
    composite[row.criterion] = extractVersionFields(row.snapshot);
  }
  return composite;
}

async function fetchSessionAiBaselineRows(
  sessionId: string,
  assessmentType: 'pilot' | 'instructor',
): Promise<PilotAiBaselineRow[] | InstructorAiBaselineRow[]> {
  if (assessmentType === 'pilot') {
    const { data: assessments, error: assessError } = await supabase
      .from('assessments_pilot')
      .select('id, pilot_role, competency')
      .eq('session_id', sessionId);

    if (assessError) throw assessError;
    const ids = (assessments ?? []).map((a) => a.id);
    if (ids.length === 0) return [];

    const { data: versions, error: versionError } = await supabase
      .from('assessment_versions')
      .select('assessment_id, snapshot, created_at')
      .in('assessment_id', ids)
      .eq('assessment_type', 'pilot')
      .eq('is_ai_baseline', true);

    if (versionError) throw versionError;

    const byId = new Map((assessments ?? []).map((a) => [a.id, a]));
    const rows: PilotAiBaselineRow[] = [];
    for (const v of versions ?? []) {
      const a = byId.get(v.assessment_id);
      if (!a) continue;
      rows.push({
        pilot_role: a.pilot_role,
        competency: a.competency,
        snapshot: v.snapshot as Record<string, unknown>,
        created_at: v.created_at,
      });
    }
    return rows;
  }

  const { data: assessments, error: assessError } = await supabase
    .from('assessments_instructor')
    .select('id, criterion')
    .eq('session_id', sessionId);

  if (assessError) throw assessError;
  const ids = (assessments ?? []).map((a) => a.id);
  if (ids.length === 0) return [];

  const { data: versions, error: versionError } = await supabase
    .from('assessment_versions')
    .select('assessment_id, snapshot, created_at')
    .in('assessment_id', ids)
    .eq('assessment_type', 'instructor')
    .eq('is_ai_baseline', true);

  if (versionError) throw versionError;

  const byId = new Map((assessments ?? []).map((a) => [a.id, a]));
  const rows: InstructorAiBaselineRow[] = [];
  for (const v of versions ?? []) {
    const a = byId.get(v.assessment_id);
    if (!a) continue;
    rows.push({
      criterion: a.criterion,
      snapshot: v.snapshot as Record<string, unknown>,
      created_at: v.created_at,
    });
  }
  return rows;
}

function createSyntheticAiBaselineVersion(
  sessionId: string,
  assessmentType: 'pilot' | 'instructor',
  snapshot: Record<string, unknown>,
  createdAt: string,
): AssessmentVersion {
  return {
    id: AI_BASELINE_COMPOSITE_ID,
    assessment_id: null,
    session_id: sessionId,
    assessment_type: assessmentType,
    version_number: AI_BASELINE_VERSION_NUMBER,
    snapshot,
    is_ai_baseline: true,
    created_by: null,
    created_at: createdAt,
    change_summary: 'AI generated baseline',
  };
}

export interface CreateVersionOptions {
  isAiBaseline?: boolean;
  changeSummary?: string;
  /**
   * When provided, the RPC applies these fields to the source table atomically
   * in the same transaction instead of relying on a prior UPDATE call.
   * Only valid for assessment_type 'pilot' and 'instructor'.
   */
  updates?: AssessmentUpdateFields;
  /**
   * When true, fields are written as-is (including NULL), enabling a full
   * snapshot restore. When false (default), COALESCE keeps existing DB values
   * for any NULL param — safe for partial edits where only changed fields
   * are sent.
   */
  isRestore?: boolean;
  /**
   * Session UUID for session-level versioning (pilot and instructor). When
   * provided, the RPC snapshots ALL rows for the session (pilot: pilot_role →
   * competency; instructor: criterion → fields).
   */
  sessionId?: string;
  /**
   * Apply multiple row updates in one transaction before building the session
   * snapshot (mobile saveAll). Requires sessionId.
   */
  batchUpdates?: BatchAssessmentUpdate[];
}

/**
 * Inserts a new immutable snapshot into assessment_versions via the
 * create_assessment_version RPC.
 *
 * When options.updates is provided, the RPC also applies those field
 * updates to assessments_pilot/instructor and syncs the version column —
 * all in one transaction, eliminating the version drift window.
 *
 * When options.updates is absent, pass payload as the snapshot (used by
 * the AI pipeline and debriefing guide flows).
 *
 * Returns null on failure — callers that use updates must treat null as
 * an error and throw rather than silently continuing.
 */
export async function createVersion(
  assessmentId: string | null | undefined,
  assessmentType: AssessmentType,
  payload: Record<string, unknown> | null,
  userId: string | null,
  options: CreateVersionOptions = {}
): Promise<AssessmentVersion | null> {
  try {
    const { updates, batchUpdates } = options;
    const usesBatch = Boolean(batchUpdates && batchUpdates.length > 0);
    const usesFieldUpdates = Boolean(updates && !usesBatch);

    const { data, error } = await supabase
      .rpc('create_assessment_version', {
        p_assessment_id: assessmentId ?? null,
        p_assessment_type: assessmentType,
        p_snapshot: usesFieldUpdates ? null : payload,
        p_created_by: userId,
        p_is_ai_baseline: options.isAiBaseline ?? false,
        p_change_summary: options.changeSummary ?? null,
        p_score: usesFieldUpdates ? (updates?.score ?? null) : null,
        p_analysis: usesFieldUpdates ? (updates?.analysis ?? null) : null,
        p_goals: usesFieldUpdates ? (updates?.goals ?? null) : null,
        p_evidence: usesFieldUpdates && updates?.evidence !== undefined ? updates.evidence : null,
        p_is_restore: options.isRestore ?? false,
        p_session_id: options.sessionId ?? null,
        p_batch_updates: usesBatch ? buildRpcBatchUpdateItems(batchUpdates!) : null,
      })
      .select()
      .single();

    if (error) {
      console.error('[assessment-versions] Failed to create version via RPC:', error);
      return null;
    }

    return data as AssessmentVersion;
  } catch (err) {
    console.error('[assessment-versions] Unexpected error in createVersion:', err);
    return null;
  }
}

/**
 * Returns all versions for an assessment ordered by version_number DESC.
 *
 * For pilot/instructor session-level versioning, pass sessionId. When sessionId
 * is provided, returns composite session-level snapshots (session_id IS NOT NULL).
 */
export async function getVersions(
  assessmentId: string | undefined,
  assessmentType: AssessmentType,
  sessionId?: string,
): Promise<AssessmentVersion[]> {
  if (!assessmentId && !sessionId) return [];

  let query = supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_type', assessmentType);

  if (sessionId) {
    query = query.eq('session_id', sessionId);
  } else {
    query = query.eq('assessment_id', assessmentId!);
  }

  const { data, error } = await query.order('version_number', { ascending: false });
  if (error) throw error;
  return (data ?? []) as AssessmentVersion[];
}

/**
 * Session manual versions plus a synthetic AI Original (version_number 0) built
 * from per-row is_ai_baseline snapshots. No DB migration required.
 */
export async function getSessionVersionsWithAiBaseline(
  sessionId: string,
  assessmentType: 'pilot' | 'instructor',
): Promise<AssessmentVersion[]> {
  const sessionVersions = await getVersions(undefined, assessmentType, sessionId);

  // Session-scoped AI baseline (migration 212+)
  if (sessionVersions.some((v) => v.is_ai_baseline)) {
    return sessionVersions;
  }

  const manual = sessionVersions;
  const aiRows = await fetchSessionAiBaselineRows(sessionId, assessmentType);

  if (aiRows.length === 0) {
    return manual;
  }

  const snapshot =
    assessmentType === 'pilot'
      ? buildPilotAiCompositeSnapshot(aiRows as PilotAiBaselineRow[])
      : buildInstructorAiCompositeSnapshot(aiRows as InstructorAiBaselineRow[]);

  const createdAt = aiRows.reduce(
    (earliest, row) => (row.created_at < earliest ? row.created_at : earliest),
    aiRows[0]!.created_at,
  );

  const synthetic = createSyntheticAiBaselineVersion(
    sessionId,
    assessmentType,
    snapshot,
    createdAt,
  );

  return [...manual, synthetic].sort((a, b) => b.version_number - a.version_number);
}

/**
 * Returns a specific version by version_number, or null if not found.
 */
export async function getVersion(
  assessmentId: string,
  assessmentType: AssessmentType,
  versionNumber: number
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .eq('version_number', versionNumber)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}

/**
 * Returns the CURRENT AI-generated baseline version (is_ai_baseline = true and
 * not superseded), or null if not found.
 *
 * MNT-33: the superseded filter is what keeps this single-valued. Since
 * migration 293 a session can hold several is_ai_baseline rows, all sharing the
 * assessment_id of the same trigger row, and without the filter `.maybeSingle()`
 * would start erroring the moment any session is regenerated.
 */
export async function getAiBaseline(
  assessmentId: string,
  assessmentType: AssessmentType
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .eq('is_ai_baseline', true)
    .is('superseded_at', null)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}

/**
 * Returns the most recent version (highest version_number), or null if none exist.
 */
export async function getLatestVersion(
  assessmentId: string,
  assessmentType: AssessmentType
): Promise<AssessmentVersion | null> {
  const { data, error } = await supabase
    .from('assessment_versions')
    .select('*')
    .eq('assessment_id', assessmentId)
    .eq('assessment_type', assessmentType)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as AssessmentVersion | null;
}
